import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/context/ThemeContext";
import { api, formatApiError } from "@/lib/api";
import CourtCard from "@/components/CourtCard";
import { SPORT_COLORS, hexToRgba, CITIES } from "@/lib/utils";
import { ArrowRight, Activity, MapPin, ShieldCheck, Search, Navigation, Clock } from "lucide-react";
import { toast } from "sonner";

const HERO_BG_DARK =
  "https://images.unsplash.com/photo-1558453975-d6c57ab62b20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBjb3VydCUyMGFlcmlhbCUyMG5pZ2h0fGVufDB8fHx8MTc3ODYyMTA3MXww&ixlib=rb-4.1.0&q=85";
const HERO_BG_LIGHT =
  "https://images.unsplash.com/photo-1531861218190-f90c89febf69?q=85&w=2400&auto=format&fit=crop";

const SPORTS = ["all", "football", "basketball", "tennis", "padel", "pickleball"];

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const heroBg = theme === "light" ? HERO_BG_LIGHT : HERO_BG_DARK;
  const [courts, setCourts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [searchSport, setSearchSport] = useState("all");
  const [searchDate, setSearchDate] = useState("");
  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const [nearMeLoading, setNearMeLoading] = useState(false);

  useEffect(() => {
    api.get("/courts").then((r) => setCourts(r.data.courts || r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchCity) params.set("city", searchCity);
    if (searchSport !== "all") params.set("sport", searchSport);
    if (searchQuery) params.set("search", searchQuery);
    if (searchDate) params.set("date", searchDate);
    if (searchFrom) params.set("start_hour", searchFrom);
    if (searchTo) params.set("end_hour", searchTo);
    navigate(`/search?${params.toString()}`);
  };

  const handleNearMe = () => {
    if (!navigator.geolocation) {
      navigate("/search");
      return;
    }
    setNearMeLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const params = new URLSearchParams();
        params.set("user_lat", String(pos.coords.latitude));
        params.set("user_lng", String(pos.coords.longitude));
        params.set("sort", "distance");
        if (searchSport !== "all") params.set("sport", searchSport);
        if (searchQuery) params.set("search", searchQuery);
        setNearMeLoading(false);
        navigate(`/search?${params.toString()}`);
      },
      () => {
        setNearMeLoading(false);
        navigate("/search");
      },
      { timeout: 5000 },
    );
  };

  return (
    <div className="bg-background">
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[640px] overflow-hidden">
        <img
          src={heroBg}
          alt="Sports court"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,transparent_30%,#0A0A0A_85%)]" />

        <div className="relative z-10 h-full max-w-[1400px] mx-auto flex flex-col justify-end pb-16 px-5 md:px-10">
          <div className="max-w-3xl animate-fade-up">
            <div className="label-eyebrow !text-white mb-4" data-testid="hero-eyebrow">
              {t("hero.eyebrow")}
            </div>
            <h1 className="text-white font-heading text-6xl md:text-8xl lg:text-9xl leading-[0.85] tracking-tight">
              {t("hero.title1")}
              <br />
              <span className="text-volt">{t("hero.title2")}</span>.
              <br />
              {t("hero.title3")}.
            </h1>
            <p className="text-white/70 mt-6 max-w-xl text-base md:text-lg">
              {t("hero.subtitle")}
            </p>
          </div>

          <div className="mt-10 max-w-5xl">
            <form
              onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
              className="bg-background/85 backdrop-blur-xl border border-border p-3 flex flex-col gap-3 shadow-2xl"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 bg-card border border-border px-3 py-2 shrink-0">
                  <MapPin className="h-3.5 w-3.5 text-volt" />
                  <select
                    value={searchCity}
                    onChange={(e) => setSearchCity(e.target.value)}
                    className="bg-transparent text-xs tracking-wider uppercase text-foreground/80 focus:outline-none cursor-pointer"
                  >
                    <option value="" className="bg-card">{t("search.all_cities")}</option>
                    {CITIES.map((c) => (
                      <option key={c.key} value={c.key} className="bg-card">{t("cities." + c.key)}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-1.5 bg-card border border-border px-3 py-2 shrink-0 min-w-0">
                  <Search className="h-3.5 w-3.5 text-muted-foreground/80 shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("search.placeholder_search")}
                    className="bg-transparent text-xs tracking-wider text-foreground/80 focus:outline-none w-[120px] placeholder:text-muted-foreground/60"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleNearMe}
                  disabled={nearMeLoading}
                  className="flex items-center gap-1.5 bg-card border border-border px-3 py-2 text-[10px] tracking-widest uppercase font-bold text-muted-foreground hover:text-volt hover:border-volt/50 transition-colors shrink-0 disabled:opacity-40"
                >
                  <Navigation className={`h-3 w-3 ${nearMeLoading ? "animate-spin" : ""}`} />
                  {t("search.near_me")}
                </button>

                <div className="h-6 w-px bg-border/30 mx-1 shrink-0" />

                <select
                  value={searchSport}
                  onChange={(e) => setSearchSport(e.target.value)}
                  className="bg-card border border-border text-[10px] tracking-widest uppercase px-3 py-2 focus:outline-none focus:border-volt cursor-pointer shrink-0"
                  style={searchSport !== "all" ? { borderColor: hexToRgba(SPORT_COLORS[searchSport].bg, 0.6), color: hexToRgba(SPORT_COLORS[searchSport].bg, 1) } : {}}
                >
                  <option value="all" className="bg-card">{t("search.all_sports")}</option>
                  {SPORTS.filter((s) => s !== "all").map((s) => (
                    <option key={s} value={s} className="bg-card">{t("home.sports." + s)}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 bg-card border border-border px-3 py-2 shrink-0">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground/80" />
                  <input
                    type="date"
                    value={searchDate}
                    onChange={(e) => setSearchDate(e.target.value)}
                    className="bg-transparent text-xs tracking-wider uppercase text-foreground/80 focus:outline-none cursor-pointer"
                  />
                </div>

                <select
                  value={searchFrom}
                  onChange={(e) => setSearchFrom(e.target.value)}
                  className="bg-card border border-border text-[10px] tracking-widest uppercase px-3 py-2 focus:outline-none focus:border-volt cursor-pointer shrink-0"
                >
                  <option value="" className="bg-card">{t("search.time_from")}</option>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={String(h)} className="bg-card">{h}:00</option>
                  ))}
                </select>

                <select
                  value={searchTo}
                  onChange={(e) => setSearchTo(e.target.value)}
                  className="bg-card border border-border text-[10px] tracking-widest uppercase px-3 py-2 focus:outline-none focus:border-volt cursor-pointer shrink-0"
                >
                  <option value="" className="bg-card">{t("search.time_to")}</option>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={String(h)} className="bg-card">{h}:00</option>
                  ))}
                </select>

                <div className="flex-1" />

                <button
                  type="submit"
                  className="btn-volt px-6 py-2 flex items-center gap-2 text-xs shrink-0"
                >
                  <Search className="h-4 w-4" />
                  {t("search.find_courts")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FEATURED COURTS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="label-eyebrow text-volt mb-2">{t("home.featured_eyebrow")}</div>
            <h2 className="font-heading text-5xl md:text-6xl tracking-tight">
              {t("home.featured_title")}
            </h2>
          </div>
          <Link
            to="/search"
            data-testid="view-all-courts-link"
            className="text-xs tracking-[0.25em] uppercase font-bold flex items-center gap-2 border-b border-volt/50 pb-1 hover:text-volt transition-colors"
          >
            {t("home.view_all")} <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courts.slice(0, 6).map((c, i) => (
            <CourtCard key={c.id} court={c} index={i} />
          ))}
        </div>
      </section>

      {/* VALUE PROPOSITION */}
      <section className="border-t border-border/30 bg-card">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 grid md:grid-cols-3 gap-1">
          {[
            { icon: Activity, k: "01", titleKey: "home.vp1_title", descKey: "home.vp1_desc" },
            { icon: MapPin, k: "02", titleKey: "home.vp2_title", descKey: "home.vp2_desc" },
            { icon: ShieldCheck, k: "03", titleKey: "home.vp3_title", descKey: "home.vp3_desc" },
          ].map((f) => (
            <div key={f.k} className="p-10 border border-border/30 bg-background hover:border-volt/40 transition-colors">
              <div className="flex items-center justify-between mb-8">
                <span className="font-mono text-xs text-muted-foreground/60">{f.k}</span>
                <f.icon className="h-5 w-5 text-volt" />
              </div>
              <h3 className="font-heading text-3xl tracking-tight mb-3">{t(f.titleKey)}</h3>
              <p className="text-muted-foreground/80 text-sm leading-relaxed">{t(f.descKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OWNERS CTA */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-eyebrow text-volt mb-3">{t("home.owner_eyebrow")}</div>
            <h2 className="font-heading text-5xl md:text-6xl tracking-tight leading-[0.9]">
              {t("home.owner_title1")}
              <br />
              <span className="text-volt">{t("home.owner_title2")}</span>
            </h2>
            <p className="text-muted-foreground/80 mt-6 max-w-md">
              {t("home.owner_desc")}
            </p>
            <Link to="/register" data-testid="owner-cta-link">
              <button className="btn-volt mt-8 px-8 py-4 text-xs">{t("home.owner_cta")}</button>
            </Link>
          </div>
          <div className="aspect-[5/4] overflow-hidden border border-border">
            <img
              src="https://images.unsplash.com/photo-1517747614396-d21a78b850e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxzcG9ydHMlMjBjb3VydCUyMGFlcmlhbCUyMG5pZ2h0fGVufDB8fHx8MTc3ODYyMTA3MXww&ixlib=rb-4.1.0&q=85"
              className="w-full h-full object-cover"
              alt="Football pitch"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
